import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Grid,
  Rating,
  Button,
  useMediaQuery,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import color from "../utils/Colors";
import { Delete, ShoppingCart } from "@mui/icons-material";
import { getDataFromTable } from "../../services/services";
import { useUser } from "../../services/Context/UserContext";
const PUBLIC_BASE_URL = process.env.REACT_APP_PUBLIC_BASE_URL

interface CourseCardProps {
  courseName: string;
  teachers: string[];
  localRating: number;
  globalRating: number;
  price: number;
  level: string;
  duration: string;
  image: string;
  onRemove?: () => void;
  courseId?: number;
  unitOfPrice?: string;
}

const CourseCardWishlist: React.FC<CourseCardProps> = ({
  courseName,
  teachers,
  localRating,
  globalRating,
  price,
  level,
  duration,
  image,
  onRemove,
  courseId,
  unitOfPrice,
}) => {
  const { user } = useUser();
  const [computedLocalRating, setComputedLocalRating] = useState<number>(
    localRating || 0,
  );
  const [computedGlobalRating, setComputedGlobalRating] = useState<number>(
    globalRating || 0,
  );

  useEffect(() => {
    if (!courseId) return;
    getDataFromTable("userRatings", { feedbackType: `course_${courseId}` })
      .then((res: any) => {
        const data: any[] = res?.data?.data || [];
        if (data.length > 0) {
          const avg =
            data.reduce((sum, item) => sum + (item.rating || 0), 0) /
            data.length;
          setComputedGlobalRating(avg);

          if (user?.id) {
            const userRatingItem = data.find((item) => item.userId === user.id);
            if (userRatingItem) {
              setComputedLocalRating(userRatingItem.rating || 0);
            } else {
              setComputedLocalRating(0);
            }
          }
        } else {
          setComputedGlobalRating(0);
          setComputedLocalRating(0);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch ratings in CourseCardWishlist:", err);
      });
  }, [courseId, user?.id]);
  const isSmallScreen = useMediaQuery("(max-width:900px)");

  return (
    <Card
      sx={{
        maxHeight: isSmallScreen ? 600 : 270,
        boxShadow: "10px 20px 20px rgba(0, 0, 0, 0.04)",
        borderRadius: "10px",
        display: "flex",
        flexDirection: isSmallScreen ? "column" : "row",
        maxWidth: 900,
        width: isSmallScreen ? "400px" : "100%",
        border: "solid 2px",
        borderColor: color.secondColor,
      }}
    >
      <CardMedia
        component="img"
        sx={{
          width: isSmallScreen ? "100%" : "300px",
          //   width:  "300px",
          borderRadius: "10px",
          height: "300px",
          //   objectFit:'contain'
        }}
        image={PUBLIC_BASE_URL+image}
        alt={courseName}
      />
      <CardContent
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: isSmallScreen ? "column" : "row",
          width: "100%",
          alignItems: isSmallScreen ? "flex-start" : "center",
          pl: 3,
          pr: 4,
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Typography
            gutterBottom
            variant="h5"
            component="div"
            sx={{
              color: color.textColor,
              mt: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
            fontWeight="bold"
          >
            {courseName}
          </Typography>

          <Typography variant="body2" color="textSecondary" mb={1}>
            <strong>Teachers Name:</strong>
          </Typography>

          <Grid container spacing={1}>
            {teachers.map((teacher, index) => (
              <Grid item xs={6} key={index}>
                <Typography variant="body2" color="textSecondary">
                  {index + 1}. {teacher}
                </Typography>
              </Grid>
            ))}
          </Grid>

          <Box my={2}>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ display: "flex", alignItems: "center", mb: 1 }}
            >
              Local rating:
              <Rating
                value={computedLocalRating}
                precision={0.5}
                readOnly
                icon={<StarIcon fontSize="inherit" />}
                emptyIcon={<StarBorderIcon fontSize="inherit" />}
                sx={{ ml: 1.7 }}
              />
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ display: "flex", alignItems: "center" }}
            >
              Global rating:
              <Rating
                value={computedGlobalRating}
                precision={0.5}
                readOnly
                icon={<StarIcon fontSize="inherit" />}
                emptyIcon={<StarBorderIcon fontSize="inherit" />}
                sx={{ color: color.textColor, ml: 1 }}
              />
            </Typography>
          </Box>

          <Box
            display="flex"
            // justifyContent="space-between"
            alignItems="flex-start"
            sx={{ gap: "10px", pl: 0 }}
          >
            <Box display="flex" alignItems="center">
              <SignalCellularAltIcon fontSize="small" />
              <Typography
                variant="body2"
                ml={1}
                sx={{ color: color.textColor }}
              >
                {level}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <AccessTimeIcon fontSize="small" />
              <Typography
                variant="body2"
                ml={1}
                sx={{ color: color.textColor }}
              >
                {duration}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection={"column"}
          sx={{ gap: "10px", width: "100%", mt: isSmallScreen ? 2 : 0 }}
        >
          <Typography
            variant="h6"
            sx={{
              color: color.textColor,
              display: "flex",
              // justifyContent: "flex-end",
            }}
          >
            Price: {unitOfPrice === "INR" ? "₹" : "$"}
            {price}
          </Typography>

          <Button
            style={{
              background: color.firstColor,
              color: "white",
              borderRadius: "8px",
              textTransform: "none",
              transition: "transform 0.3s ease",
            }}
            onMouseEnter={(e) => {
              const target = e.target as HTMLButtonElement;
              target.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              const target = e.target as HTMLButtonElement;
              target.style.transform = "scale(1)";
            }}
          >
            Move to Cart <ShoppingCart sx={{ ml: 1 }}></ShoppingCart>
          </Button>

          <Button
            style={{
              background: color.firstColor,
              color: "white",
              borderRadius: "8px",
              textTransform: "none",
              transition: "transform 0.3s ease",
            }}
            onClick={onRemove}
            onMouseEnter={(e) => {
              const target = e.target as HTMLButtonElement;
              target.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              const target = e.target as HTMLButtonElement;
              target.style.transform = "scale(1)";
            }}
          >
            Remove from Wishlist <Delete sx={{ ml: 1 }}></Delete>
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CourseCardWishlist;
